import React, { useEffect, useMemo, useState } from 'react';

const levelClasses = {
  ERROR: 'label-light-danger',
  CRITICAL: 'label-light-danger',
  ALERT: 'label-light-danger',
  EMERGENCY: 'label-light-danger',
  WARNING: 'label-light-warning',
  NOTICE: 'label-light-info',
  INFO: 'label-light-primary',
  DEBUG: 'label-light-secondary',
};

export default function LaravelLogViewer({ 'instance-name': instanceName }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ date: '', from: '', to: '', limit: 150 });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });

    return params.toString();
  }, [filters]);

  const fetchEntries = () => {
    setLoading(true);
    setError('');

    fetch(`/log/${instanceName}/entries?${queryString}`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('No fue posible cargar laravel.log');
        }

        return response.json();
      })
      .then((data) => {
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setExpanded({});
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEntries();
  }, [queryString]);

  const updateFilter = (key, value) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
      ...(key === 'date' && value ? { from: '', to: '' } : {}),
      ...((key === 'from' || key === 'to') && value ? { date: '' } : {}),
    }));
  };

  const clearFilters = () => {
    setFilters({ date: '', from: '', to: '', limit: 150 });
  };

  const toggleEntry = (index) => {
    setExpanded((currentExpanded) => ({
      ...currentExpanded,
      [index]: !currentExpanded[index],
    }));
  };

  return (
    <div className="card card-custom gutter-b">
      <div className="card-header flex-wrap py-5">
        <div className="card-title">
          <h3 className="card-label mb-0">laravel.log</h3>
        </div>
        <div className="card-toolbar">
          <button type="button" className="btn btn-light-primary btn-sm" onClick={fetchEntries} disabled={loading}>
            {loading ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>
      </div>

      <div className="card-body">
        <div className="form-row align-items-end mb-6">
          <div className="form-group col-md-3">
            <label>Fecha exacta</label>
            <input type="date" className="form-control" value={filters.date} onChange={(event) => updateFilter('date', event.target.value)} />
          </div>
          <div className="form-group col-md-3">
            <label>Desde</label>
            <input type="date" className="form-control" value={filters.from} onChange={(event) => updateFilter('from', event.target.value)} />
          </div>
          <div className="form-group col-md-3">
            <label>Hasta</label>
            <input type="date" className="form-control" value={filters.to} onChange={(event) => updateFilter('to', event.target.value)} />
          </div>
          <div className="form-group col-md-2">
            <label>Límite</label>
            <input type="number" min="10" max="500" className="form-control" value={filters.limit} onChange={(event) => updateFilter('limit', event.target.value)} />
          </div>
          <div className="form-group col-md-1">
            <button type="button" className="btn btn-light btn-block" onClick={clearFilters}>Limpiar</button>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <span className="text-muted">Instancia: <strong>{instanceName}</strong></span>
          <span className="text-muted">{total} entradas encontradas</span>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {!loading && entries.length === 0 && !error && <div className="alert alert-secondary">No hay entradas para los filtros seleccionados.</div>}

        <div className="log-viewer-list">
          {entries.map((entry, index) => {
            const isExpanded = expanded[index] !== false;
            const badgeClass = levelClasses[entry.level] || 'label-light-dark';

            return (
              <div className="border rounded p-4 mb-3" key={`${entry.datetime}-${index}`}>
                <div className="d-flex align-items-start justify-content-between">
                  <button type="button" className="btn btn-link p-0 text-left" onClick={() => toggleEntry(index)}>
                    <span className={`label ${badgeClass} label-inline mr-3`}>{entry.level}</span>
                    <span className="font-weight-bold text-dark">{entry.datetime}</span>
                    <span className="text-muted ml-3">{entry.environment}</span>
                  </button>
                  <button type="button" className="btn btn-light btn-sm" onClick={() => toggleEntry(index)}>
                    {isExpanded ? 'Minimizar' : 'Ver'}
                  </button>
                </div>

                <div className="text-dark mt-3">{entry.message}</div>

                {isExpanded && (
                  <pre className="bg-light p-4 mt-3 mb-0 rounded" style={{ whiteSpace: 'pre-wrap', maxHeight: 420, overflow: 'auto' }}>
                    {entry.raw}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
